import { cn } from '@amore/ui/cn'
import { Button } from '@amore/ui/components/button'
import { DatePicker } from '@amore/ui/components/date-picker'
import { Input } from '@amore/ui/components/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@amore/ui/components/tooltip'
import { BedDouble, Car, MessageSquareText, Plus, Salad, Trash2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { newId, plural } from '@/lib/format'
import { replaceById, useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { DayPart, GuestSettings } from '@/mock/types'
import { Segmented } from '@/patterns/detail'
import { ConfirmDialog } from '@/patterns/dialog'
import { datePickerLabels } from '../tasks/task-fields'

const field = 'h-10 border-transparent bg-muted/60 hover:bg-muted focus:bg-card'

/**
 * "Nastavení": the rules behind the guest list and the RSVP form. Saves as
 * you go (no save button); each section is a label on the left and its live
 * controls on the right, separated by thin lines.
 */
export function OrganizationPage() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { dayParts, guestSettings: settings, website } = data
  const [removing, setRemoving] = useState<DayPart | null>(null)

  const setParts = (next: DayPart[]) => update((current) => ({ ...current, dayParts: next }))
  const setSettings = (patch: Partial<GuestSettings>) =>
    update((current) => ({ ...current, guestSettings: { ...current.guestSettings, ...patch } }))
  const setAsk = (key: keyof GuestSettings['ask']) =>
    setSettings({ ask: { ...settings.ask, [key]: !settings.ask[key] } })
  const invitedTo = (part: DayPart) =>
    data.parties.filter((party) => party.parts.includes(part.id)).length
  const sortByTime = () =>
    setParts([...dayParts].sort((a, b) => (a.time ?? '99').localeCompare(b.time ?? '99')))
  const removePart = (part: DayPart) => {
    update((current) => ({
      ...current,
      dayParts: current.dayParts.filter((item) => item.id !== part.id),
      parties: current.parties.map((party) => ({
        ...party,
        parts: party.parts.filter((id) => id !== part.id),
      })),
    }))
    setRemoving(null)
  }

  return (
    <div className="divide-y divide-border rounded-panel border border-border bg-card px-6 sm:px-8">
      <Row
        title="Části dne"
        description="Na co hosty zvete. U každé pozvánky pak vyberete, které části se jí týkají."
      >
        <fieldset disabled={!canEdit} aria-label="Části dne" className="space-y-2">
          {dayParts.map((part) => (
            <div key={part.id} className="flex items-center gap-2">
              <Input
                type="time"
                value={part.time ?? ''}
                aria-label={`Čas: ${part.label}`}
                onChange={(event) =>
                  setParts(
                    replaceById(dayParts, part.id, { time: event.target.value || undefined }),
                  )
                }
                onBlur={sortByTime}
                className={cn(field, 'w-28 shrink-0 tabular-nums')}
              />
              <Input
                value={part.label}
                maxLength={40}
                aria-label="Název části dne"
                placeholder="Např. Obřad"
                onChange={(event) =>
                  setParts(replaceById(dayParts, part.id, { label: event.target.value }))
                }
                className={cn(field, 'min-w-0 flex-1')}
              />
              <span className="hidden w-24 shrink-0 text-right text-xs text-muted-foreground sm:block">
                {invitations(invitedTo(part))}
              </span>
              {canEdit ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      appearance="ghost"
                      tone="neutral"
                      size="icon"
                      className="size-10 shrink-0"
                      onClick={() => (invitedTo(part) > 0 ? setRemoving(part) : removePart(part))}
                    >
                      <Trash2 aria-hidden="true" className="size-4" />
                      <span className="sr-only">Odebrat část dne</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Odebrat část dne</TooltipContent>
                </Tooltip>
              ) : null}
            </div>
          ))}
          {canEdit ? (
            <Button
              size="sm"
              appearance="outline"
              tone="neutral"
              className="mt-2"
              onClick={() => setParts([...dayParts, { id: newId('part'), label: '' }])}
            >
              <Plus aria-hidden="true" className="size-3.5" />
              Přidat část dne
            </Button>
          ) : null}
        </fieldset>
      </Row>

      <Row
        title="Odpovědi přes web"
        description="Hosté odpovídají přes svatební odkaz. Po termínu už odpověď nezmění; napíšou vám."
      >
        <div className="flex flex-wrap items-end gap-6">
          <Labeled label="Přijímat odpovědi">
            <Segmented
              label="Přijímat odpovědi"
              options={[
                { value: 'on', label: 'Ano' },
                { value: 'off', label: 'Ne' },
              ]}
              value={website.rsvpEnabled ? 'on' : 'off'}
              onChange={(value) =>
                update((current) => ({
                  ...current,
                  website: { ...current.website, rsvpEnabled: value === 'on' },
                }))
              }
              disabled={!canEdit}
            />
          </Labeled>
          <Labeled label="Odpovědět do">
            <DatePicker
              value={website.rsvpDeadline ?? ''}
              onValueChange={(value) =>
                update((current) => ({
                  ...current,
                  website: { ...current.website, rsvpDeadline: value || null },
                }))
              }
              readOnly={!canEdit}
              labels={{
                ...datePickerLabels,
                placeholder: 'Bez termínu',
                title: 'Termín pro odpovědi',
              }}
              className={cn(field, 'w-52')}
              formatDate={(date) =>
                date.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' })
              }
              formatMonth={(date) =>
                date.toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' })
              }
            />
          </Labeled>
        </div>
      </Row>

      <Row
        title="Na co se ptáme"
        description="Účast se ptáme vždy. K tomu vyberte, co mají hosté ve formuláři vyplnit."
      >
        <div className="flex flex-wrap gap-2">
          <Toggle icon={Salad} pressed={settings.ask.diets} onClick={() => setAsk('diets')}>
            Strava
          </Toggle>
          <Toggle
            icon={BedDouble}
            pressed={settings.ask.accommodation}
            onClick={() => setAsk('accommodation')}
          >
            Ubytování
          </Toggle>
          <Toggle icon={Car} pressed={settings.ask.transport} onClick={() => setAsk('transport')}>
            Odvoz
          </Toggle>
          <Toggle
            icon={MessageSquareText}
            pressed={settings.ask.message}
            onClick={() => setAsk('message')}
          >
            Vzkaz
          </Toggle>
        </div>
      </Row>

      <Row title="Doprovod a děti" description="Co hosté uvidí, když budou vyplňovat, kdo přijde.">
        <div className="space-y-6">
          <Labeled label="Doprovod">
            <Segmented
              label="Doprovod"
              options={[
                { value: 'listed', label: 'Jen kde ho přidáte' },
                { value: 'anyone', label: 'Kdokoli si může přivést' },
              ]}
              value={settings.companions}
              onChange={(companions) => setSettings({ companions })}
              disabled={!canEdit}
            />
          </Labeled>
          <Labeled label="Děti">
            <Segmented
              label="Děti"
              options={[
                { value: 'listed', label: 'Jen děti na pozvánce' },
                { value: 'welcome', label: 'Můžou přivést své děti' },
                { value: 'none', label: 'Bez dětí' },
              ]}
              value={settings.children}
              onChange={(children) => setSettings({ children })}
              disabled={!canEdit}
            />
          </Labeled>
        </div>
      </Row>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null)
        }}
        heading={`Odebrat ${removing?.label || 'část dne'}?`}
        description={
          removing
            ? `Je na ni pozváno ${invitations(invitedTo(removing))}. Z jejich pozvání zmizí.`
            : ''
        }
        keepLabel="Ponechat"
        confirmLabel="Odebrat část dne"
        onConfirm={() => removing && removePart(removing)}
      />
    </div>
  )
}

const invitations = (count: number) =>
  `${count} ${plural(count, 'pozvánka', 'pozvánky', 'pozvánek')}`

/** One setting: what it is on the left, its live controls on the right. */
function Row({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <section className="grid gap-4 py-7 md:grid-cols-[16rem_minmax(0,1fr)] md:gap-10">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="min-w-0">{children}</div>
    </section>
  )
}

function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2.5">
      <span className="block text-sm text-muted-foreground">{label}</span>
      {children}
    </div>
  )
}

function Toggle({
  icon: Icon,
  pressed,
  onClick,
  children,
}: {
  icon: typeof Salad
  pressed: boolean
  onClick: () => void
  children: ReactNode
}) {
  const canEdit = useCanEdit()
  return (
    <Button
      size="sm"
      appearance="outline"
      tone="neutral"
      aria-pressed={pressed}
      disabled={!canEdit}
      onClick={onClick}
    >
      <Icon aria-hidden="true" className="size-3.5" />
      {children}
    </Button>
  )
}
